import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Card, CardContent, CardHeader, Typography, Paper, Divider, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

const CodeBlock: React.FC<{ children: string }> = ({ children }) => (
    <Paper
        variant="outlined"
        sx={{
            p: 1.5,
            backgroundColor: '#f5f5f5',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            whiteSpace: 'pre',
            overflowX: 'auto'
        }}
    >
        {children}
    </Paper>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>{children}</Typography>
);

interface FieldRow {
    name: string;
    type: string;
    description: string;
    masked?: boolean;
}

const refundFields: FieldRow[] = [
    { name: 'id', type: 'long', description: 'Внутренний ID возврата.' },
    { name: 'terminalId', type: 'long', description: 'Глобальный ID терминала, на котором был открыт (создан) возврат.' },
    { name: 'terminalName', type: 'string?', description: 'Имя терминала открытия (берётся из таблицы Terminals).' },
    { name: 'info', type: 'string?', description: 'Произвольная информация о возврате.' },
    { name: 'text', type: 'string?', description: 'Произвольный текст возврата.' },
    { name: 'dateTime', type: 'datetime', description: 'Дата и время создания возврата (ISO 8601).' },
    { name: 'sum', type: 'decimal', description: 'Сумма возврата.' },
    { name: 'state', type: 'string', description: 'Статус: "Ok", "TemporarilyBlocked", "Blocked".' },
    { name: 'cardNo', type: 'string?', description: 'Номер карты, по которой выполнен возврат.' },
    { name: 'lastManualOp', type: 'datetime?', description: 'Время последней ручной операции с возвратом (включая Block/Unblock).' },
    { name: 'closeDateTime', type: 'datetime?', description: 'Дата закрытия возврата. Заполняется значением lastManualOp, если state = "Blocked"; иначе null.' },
    { name: 'refundCode', type: 'string?', description: 'Уникальный код возврата.', masked: true },
    { name: 'closeOnTerminalId', type: 'long?', description: 'Глобальный ID терминала, на котором возврат был закрыт. null, если возврат ещё не закрывался.' },
    { name: 'externalId', type: 'string?', description: 'Внешний идентификатор для интеграций.', masked: true },
    { name: 'properties', type: 'object?', description: 'Произвольные пары ключ/значение (string→string), привязанные к возврату. См. ниже.' },
    { name: 'creationRrn', type: 'string?', description: 'RRN заказа, создавшего ваучер.' },
    { name: 'closeRRN', type: 'string?', description: 'RRN заказа, погасившего ваучер.' }
];

const queryParams: FieldRow[] = [
    { name: 'dateFrom', type: 'datetime?', description: 'Нижняя граница диапазона по дате возврата (включительно).' },
    { name: 'dateTo', type: 'datetime?', description: 'Верхняя граница диапазона по дате возврата (включительно).' },
    { name: 'state', type: 'string?', description: 'Фильтр по статусу: "Ok", "TemporarilyBlocked", "Blocked".' },
    { name: 'terminalName', type: 'string?', description: 'Подстрочный поиск по имени терминала (LIKE %...%).' },
    { name: 'cardNo', type: 'string?', description: 'Подстрочный поиск по номеру карты (LIKE %...%).' },
    { name: 'refundCode', type: 'string?', description: 'Точный поиск по уникальному коду возврата. Поддерживается также CSV-эндпоинтом.' },
    { name: 'pageNumber', type: 'int', description: 'Номер страницы (по умолчанию 1). Только для JSON эндпоинта.' },
    { name: 'pageSize', type: 'int', description: 'Размер страницы (по умолчанию 50, максимум 1000). Только для JSON эндпоинта.' }
];

const graphFields: FieldRow[] = [
    { name: 'rootRefundId', type: 'long?', description: 'ID ваучера, от которого построен граф. Для поиска по операции — null.' },
    { name: 'operation', type: 'VoucherOperation?', description: 'Операция из параметров запроса. Для графа ваучера — null.' },
    { name: 'vouchers', type: 'VoucherNode[]', description: 'Все ваучеры связанного компонента.' },
    { name: 'operations', type: 'VoucherOperationSummary[]', description: 'Заказы с входными и выходными ваучерами и итогами по суммам.' },
    { name: 'relations', type: 'VoucherRelation[]', description: 'Совместимые явные связи из parentVouchers.' },
    { name: 'warnings', type: 'string[]', description: 'Предупреждения о неразрешённых родителях или недостаточных данных.' }
];

export const ApiDocsPage: React.FC = () => {
    return (
        <Box sx={{ width: '100%', p: 2 }}>
            <Card>
                <CardHeader
                    title="Документация внешнего API"
                    subheader="Доступ через X-Api-Key, управление ключами на странице «API ключи»"
                    action={
                        <Button
                            component={RouterLink}
                            to="/api-keys"
                            startIcon={<ArrowBack />}
                            variant="outlined"
                        >
                            К API ключам
                        </Button>
                    }
                />
                <CardContent>
                    <Typography variant="body2" color="textSecondary">
                        Внешнее API предназначено для выгрузки данных о возвратах сторонними системами.
                        Все эндпоинты требуют авторизации через персональный API-ключ. Создать и
                        отозвать ключ можно на странице <RouterLink to="/api-keys">API ключи</RouterLink>.
                    </Typography>

                    <SectionTitle>Авторизация</SectionTitle>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Передавайте API-ключ в заголовке HTTP-запроса:
                    </Typography>
                    <CodeBlock>{'X-Api-Key: <ваш_ключ>'}</CodeBlock>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        При отсутствии или невалидном ключе сервер вернёт <code>401 Unauthorized</code>.
                        У каждого ключа есть флаг «Полные данные»: если он выключен, поля
                        <code>refundCode</code>, <code>externalId</code> и коды ваучеров внутри
                        <code>parentVouchers</code> заменяются маскированными значениями.
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    <SectionTitle>GET /api/external/refunds — JSON, постранично</SectionTitle>
                    <Typography variant="body2">
                        Возвращает страницу возвратов в формате <code>PagedResult&lt;Refund&gt;</code>.
                        Сортировка по <code>id</code> убыванию.
                    </Typography>

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Параметры запроса</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Параметр</TableCell>
                                    <TableCell>Тип</TableCell>
                                    <TableCell>Описание</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {queryParams.map(p => (
                                    <TableRow key={p.name}>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{p.name}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{p.type}</TableCell>
                                        <TableCell>{p.description}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Пример запроса</Typography>
                    <CodeBlock>
{`GET /api/external/refunds?dateFrom=2026-01-01&dateTo=2026-12-31&state=Ok&pageNumber=1&pageSize=50
X-Api-Key: <ваш_ключ>`}
                    </CodeBlock>

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Пример ответа</Typography>
                    <CodeBlock>
{`{
  "items": [
    {
      "id": 1234,
      "terminalId": 5,
      "terminalName": "Терминал-1",
      "info": "...",
      "text": "...",
      "dateTime": "2026-05-06T12:00:00",
      "sum": 100.0,
      "state": "Blocked",
      "cardNo": "1234567890",
      "lastManualOp": "2026-05-07T14:30:00",
      "closeDateTime": "2026-05-07T14:30:00",
      "refundCode": "ABC123",
      "closeOnTerminalId": 7,
      "externalId": "ORDER-42",
      "properties": { "RRN": "ORDER-41", "closeRRN": "ORDER-42" },
      "creationRrn": "ORDER-41",
      "closeRRN": "ORDER-42"
    }
  ],
  "totalCount": 1,
  "pageNumber": 1,
  "pageSize": 50,
  "totalPages": 1
}`}
                    </CodeBlock>

                    <Divider sx={{ my: 3 }} />

                    <SectionTitle>GET /api/external/refunds/csv — потоковый CSV</SectionTitle>
                    <Typography variant="body2">
                        Тот же набор фильтров, что и у JSON-эндпоинта, кроме <code>pageNumber</code>/<code>pageSize</code> —
                        возвращается весь результат потоком. Заголовки ответа:
                    </Typography>
                    <CodeBlock>
{`Content-Type: text/csv; charset=utf-8
Content-Disposition: attachment; filename=refunds.csv`}
                    </CodeBlock>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Заголовок CSV-файла:
                    </Typography>
                    <CodeBlock>
{'Id,TerminalId,TerminalName,Info,Text,DateTime,Sum,State,CardNo,RefundCode,ExternalId,CloseOnTerminalId,LastManualOp,CloseDateTime,CreationRrn,CloseRRN,Properties'}
                    </CodeBlock>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Колонка <code>Properties</code> содержит JSON-сериализацию словаря (либо пуста, если свойств нет).
                        Поля со спецсимволами экранируются по RFC 4180 (двойные кавычки удваиваются, всё значение
                        оборачивается в кавычки).
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    <SectionTitle>GET /api/external/refunds/operations — ваучеры операции</SectionTitle>
                    <Typography variant="body2">
                        Возвращает ваучеры, связанные с одной операцией по паре <code>RRN + terminalId</code>:
                        созданные этой операцией, погашенные этой операцией при наличии явного RRN погашения,
                        а также родительские ваучеры, погашение которых определяется через созданный остаток.
                        <code>terminalId</code> — глобальный идентификатор из поля <code>terminalId</code> объекта Refund.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Параметры запроса</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Параметр</TableCell>
                                    <TableCell>Тип</TableCell>
                                    <TableCell>Описание</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>rrn</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>string</TableCell>
                                    <TableCell>RRN операции, обязательный непустой параметр.</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>terminalId</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>long</TableCell>
                                    <TableCell>Глобальный ID терминала, обязательное положительное значение.</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Пример запроса</Typography>
                    <CodeBlock>
{`GET /api/external/refunds/operations?rrn=0220260618095307&terminalId=5
X-Api-Key: <ваш_ключ>`}
                    </CodeBlock>

                    <Divider sx={{ my: 3 }} />

                    <SectionTitle>GET /api/external/refunds/{'{refundId}'}/tree — граф ваучера</SectionTitle>
                    <Typography variant="body2">
                        Возвращает весь связанный компонент: ваучеры и заказы, в которых они создавались или погашались.
                        Связь заказа определяется только парой <code>terminalId + RRN</code>. Если ваучер не существует,
                        сервер вернёт <code>404 Not Found</code>.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Пример запроса</Typography>
                    <CodeBlock>
{`GET /api/external/refunds/1234/tree
X-Api-Key: <ваш_ключ>`}
                    </CodeBlock>

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Фрагмент ответа графа</Typography>
                    <CodeBlock>
{`{
  "rootRefundId": 1234,
  "operation": null,
  "vouchers": [
    {
      "refund": {
        "id": 1234,
        "terminalId": 5,
        "sum": 100.0,
        "state": "Blocked",
        "refundCode": "INPUT-001",
        "creationRrn": "PREVIOUS-ORDER",
        "closeRRN": "0220260618095307"
      },
      "creationOperation": { "rrn": "PREVIOUS-ORDER", "terminalId": 5, "isInferred": false },
      "closeOperation": {
        "rrn": "0220260618095307",
        "terminalId": 5,
        "isInferred": false
      }
    }
  ],
  "operations": [
    {
      "rrn": "0220260618095307",
      "terminalId": 5,
      "inputRefundIds": [1234, 1236],
      "outputRefundIds": [1235, 1237],
      "inferredInputRefundIds": [],
      "inputSum": 150.0,
      "outputSum": 40.0,
      "usedSum": 110.0,
      "isAmountComplete": true,
      "hasUnresolvedInputs": false
    }
  ],
  "relations": [],
  "warnings": []
}`}
                    </CodeBlock>

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Поля ответа графа</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Поле</TableCell>
                                    <TableCell>Тип</TableCell>
                                    <TableCell>Описание</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {graphFields.map(f => (
                                    <TableRow key={f.name}>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{f.name}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{f.type}</TableCell>
                                        <TableCell>{f.description}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Каждый элемент <code>vouchers</code> содержит объект <code>refund</code>,
                        <code>creationOperation</code> и <code>closeOperation</code>. Операция включает поля
                        <code>rrn</code>, <code>terminalId</code> и <code>isInferred</code>. Значение
                        <code>isInferred: true</code> означает, что RRN погашения родителя выведен из операции создания
                        дочернего ваучера на остаток.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Элемент <code>operations</code> представляет заказ: <code>inputRefundIds</code> — погашенные
                        ваучеры, <code>outputRefundIds</code> — созданные остатки. <code>usedSum</code> равен разнице
                        <code>inputSum - outputSum</code>, когда <code>isAmountComplete=true</code>. При полном погашении
                        список выходов пуст, а <code>outputSum</code> равен нулю.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Если код из <code>parentVouchers</code> не найден, связь возвращается с
                        <code>parentRefundId: null</code> и заполненным <code>unresolvedParentRefundCode</code>.
                        Для API-ключа без флага «Полные данные» этот код маскируется.
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    <SectionTitle>Поля объекта Refund</SectionTitle>
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Поле</TableCell>
                                    <TableCell>Тип</TableCell>
                                    <TableCell>Описание</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {refundFields.map(f => (
                                    <TableRow key={f.name}>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>
                                            {f.name}
                                            {f.masked && (
                                                <Chip
                                                    label="маскируется"
                                                    size="small"
                                                    color="warning"
                                                    sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{f.type}</TableCell>
                                        <TableCell>{f.description}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                        Поля, помеченные «маскируется», у ключей без флага «Полные данные» возвращаются как <code>***</code>.
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    <SectionTitle>Свойства возврата (properties)</SectionTitle>
                    <Typography variant="body2">
                        В поле <code>properties</code> хранятся произвольные пары <code>string → string</code>,
                        переданные при создании возврата или при изменении его статуса. Сервер мерджит новые
                        ключи поверх старых (последняя запись побеждает); удаления через API нет.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Ключи, которые сервер распознаёт при построении операций и связей:
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Ключ</TableCell>
                                    <TableCell>Значение</TableCell>
                                    <TableCell>Когда</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>RRN</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>string</TableCell>
                                    <TableCell>RRN операции создания ваучера. Для нового ПО допустимо имя <code>creationRrn</code>.</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>parentVouchers</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>string</TableCell>
                                    <TableCell>Коды входных ваучеров, разделённые точкой с запятой. Резервный источник для старых записей без closeRRN; маскируется у ключей без полных данных.</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>closeRRN</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>string</TableCell>
                                    <TableCell>RRN заказа погашения. Это же имя используется в API, CSV и серверной модели.</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>closeReason</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>manual</TableCell>
                                    <TableCell>Возврат заблокирован администратором через веб-интерфейс.</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="subtitle2" sx={{ mt: 3 }}>Расчёт сумм и ограничения</Typography>
                    <Typography variant="body2">
                        API рассчитывает итог <code>usedSum</code> для заказа. При нескольких входных ваучерах текущих
                        данных недостаточно, чтобы разложить этот итог по каждому ваучеру отдельно.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Версия ПО АЗС с поддержкой <code>closeRRN</code> позволяет определить и частичное, и полное
                        погашение. Историческое полное погашение без <code>closeRRN</code> восстановить достоверно нельзя.
                        Административная блокировка без <code>closeRRN</code> не считается операцией погашения.
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    <SectionTitle>Коды ответа</SectionTitle>
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Код</TableCell>
                                    <TableCell>Описание</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>200 OK</TableCell>
                                    <TableCell>Успешный ответ.</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>401 Unauthorized</TableCell>
                                    <TableCell>API-ключ отсутствует, недействителен или отключён.</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>400 Bad Request</TableCell>
                                    <TableCell>Не передан RRN или указан некорректный terminalId при поиске операции.</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>404 Not Found</TableCell>
                                    <TableCell>Ваучер с указанным ID не найден при запросе дерева.</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};
